import { useQueryClient } from '@tanstack/react-query'
import { notification } from 'antd'
import { useCallback } from 'react'
import useUpsertCustomerConfiguration from '../../Configuration/hooks/useUpsertCustomerConfiguration'
import { useOepConfigurations } from './useOepConfigurations'
import { OEP_CONFIG_KEYS } from '../types'
import type { OepToggleKey, OepToggleState } from '../types'

const FALLBACK_STATE: OepToggleState = {
  showOpenEnrollmentTab: false,
  showPlansSubTab: false,
  showPeopleSubTab: false,
}

interface Result {
  readonly toggles: OepToggleState
  readonly isMasterOn: boolean
  readonly isLoading: boolean
  readonly isError: boolean
  readonly handleChange: (key: OepToggleKey, checked: boolean) => void
}

/**
 * Encapsulates the read + optimistic-write of the three OEP preview toggles so
 * `OepToggleSection` stays presentational. The react-query cache is the single
 * source of truth — optimistic writes go straight into it, so a background
 * refetch can't diverge from the UI.
 */
export function useOepToggles(clientId: string): Result {
  const queryClient = useQueryClient()
  const { data, isLoading, isError } = useOepConfigurations(clientId)
  const { mutate } = useUpsertCustomerConfiguration()

  const queryKey = ['oepConfigurations', clientId]
  const toggles = data ?? FALLBACK_STATE
  const isMasterOn = toggles.showOpenEnrollmentTab

  const handleChange = useCallback(
    (key: OepToggleKey, checked: boolean) => {
      // Optimistic update in the cache.
      queryClient.setQueryData<OepToggleState>(queryKey, (curr) => ({
        ...(curr ?? FALLBACK_STATE),
        [key]: checked,
      }))
      mutate(
        { customerId: clientId, key: OEP_CONFIG_KEYS[key], value: checked },
        {
          onSuccess: () => {
            // Refresh the OE consumer (nav menu + /open-enrollment routes) so a
            // saved toggle takes effect without a full page reload. Prefix match
            // (no company id) covers the clientId-vs-currentCompanyId mismatch.
            queryClient.invalidateQueries({
              queryKey: ['abp-open-enrollment-customer-configuration'],
            })
          },
          onError: () => {
            // Revert only this key, preserving any concurrent edits.
            queryClient.setQueryData<OepToggleState>(queryKey, (curr) => ({
              ...(curr ?? FALLBACK_STATE),
              [key]: !checked,
            }))
            notification.error({
              message: 'Could not update setting',
              description:
                'Your change was not saved. Please try again in a moment.',
            })
          },
        }
      )
    },
    // queryKey is derived from clientId; listing clientId keeps it stable.
    [clientId, mutate, queryClient]
  )

  return { toggles, isMasterOn, isLoading, isError, handleChange }
}
